import React, { createContext, useContext, useEffect, useState } from 'react';
import { globalHistory } from "@reach/router"

const SessionContext = createContext();

export const SessionProvider = ({ children }) => {
    const [sessionData, setSessionData] = useState({
        sessionId: '',
        originalSource: '',
        landingPage: '',
        history: [],
        utmTags: {}
    });

    useEffect(() => {
        // Client-side only
        if (typeof window === 'undefined') return;

        const STORAGE_KEY = 'rotor_session';

        // Helper to get UTMs
        function getUtms() {
            const params = new URLSearchParams(window.location.search);
            const utms = {};
            ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach(key => {
                if (params.has(key)) utms[key] = params.get(key);
            });
            return utms;
        }

        // Load or Init
        let data;
        try {
            data = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY) || '{}');
        } catch (e) {
            data = {};
        }

        // If no active session, initialize one
        if (!data.sessionId) {
            data = {
                sessionId: crypto.randomUUID ? crypto.randomUUID() : Date.now().toString(36) + Math.random().toString(36).substr(2),
                originalSource: document.referrer || 'Direct',
                landingPage: window.location.pathname,
                history: [window.location.pathname],
                utmTags: getUtms(),
                timestamp: new Date().toISOString()
            };
            window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
        }

        setSessionData(data);

        // GA4 Sync (GDPR Safe)
        function syncToGA(sessionId) {
            if (typeof window === 'undefined') return;

            // 1. Check STRICT consent (cookie name from gatsby-plugin-gdpr-cookies)
            const gaCookie = document.cookie.split('; ').find(row => row.startsWith('gatsby-gdpr-google-analytics='));
            const hasConsent = gaCookie ? gaCookie.split('=')[1] === 'true' : false;

            if (hasConsent && sessionId) {
                // 2. Check if GA is loaded
                if (typeof window.gtag === 'function') {
                    window.gtag('set', 'user_properties', {
                        rotor_session_id: sessionId
                    });
                    // Also explicitly push an event to signal session start custom param
                    window.gtag('event', 'rotor_session_start', {
                        rotor_session_id: sessionId
                    });
                } else if (typeof window.dataLayer !== 'undefined') {
                    window.dataLayer.push({
                        'rotor_session_id': sessionId
                    });
                }
            }
        }

        // Attempt sync on initial load and potential updates
        syncToGA(data.sessionId);

        // Listen for history changes to track navigation
        const removeListener = globalHistory.listen(({ location }) => {
            setSessionData(prev => {
                // Prevent duplicate entries if the router fires multiple times for same path 
                const lastPath = prev.history[prev.history.length - 1];
                if (lastPath === location.pathname) return prev;

                const newHistory = [...prev.history, location.pathname];
                const newData = { ...prev, history: newHistory };
                window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newData));
                return newData;
            });
        });

        return () => removeListener();
    }, []);

    return (
        <SessionContext.Provider value={sessionData}>
            {children}
        </SessionContext.Provider>
    );
};

export const useSession = () => useContext(SessionContext);
